// Copy of quick-add.js
// Modified to render the entire product form from the quickview product template

import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

// Centralized selector for the quickview product grid content
const PRODUCT_GRID_SELECTOR = '#td-product-information-grid';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #listenersAbortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {string | null} */
  #cachedProductUrl = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    const anchorHref = /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    )?.href;

    if (!anchorHref) return '';

    try {
      const url = new URL(anchorHref, window.location.origin);
      url.searchParams.set('view', 'quickview');
      return url.toString();
    } catch (_error) {
      // Fallback: append query param manually if URL constructor fails
      return `${anchorHref}${anchorHref.includes('?') ? '&' : '?'}view=quickview`;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Setup delegated listeners for interactions within this component instance
    this.#listenersAbortController = new AbortController();
    this.addEventListener('click', this.#handleDelegatedClick, {
      signal: this.#listenersAbortController.signal,
    });

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup delegated listeners
    this.#listenersAbortController?.abort();
    this.#listenersAbortController = null;

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentProductUrl = this.productPageUrl;
    if (!currentProductUrl) return;

    // Decide if we need to fetch (no cache, URL changed, or cache missing marker)
    let mustFetch = !this.#cachedProductHtml || this.#cachedProductUrl !== currentProductUrl;
    if (!mustFetch && this.#cachedProductHtml) {
      const hasMarker = this.#cachedProductHtml.querySelector?.(PRODUCT_GRID_SELECTOR);
      if (!hasMarker) mustFetch = true;
    }

    if (mustFetch) {
      await this.fetchProductPage(currentProductUrl);
      this.#cachedProductUrl = currentProductUrl;
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      let freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      // Validate the fresh copy contains the expected quickview content; if not, re-fetch with cache-bust once
      if (!freshHtmlCopy.querySelector(PRODUCT_GRID_SELECTOR)) {
        const cacheBustUrl = `${currentProductUrl}${currentProductUrl.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
        await this.fetchProductPage(cacheBustUrl);
        this.#cachedProductUrl = currentProductUrl;
        console.warn('QuickAdd: Cached/fetched HTML does not contain quickview content. Re-fetching with cache-bust.');
        if (this.#cachedProductHtml) {
          freshHtmlCopy = new DOMParser().parseFromString(
            this.#cachedProductHtml.documentElement.outerHTML,
            'text/html'
          );
        }
      }

      // If still invalid, abort gracefully
      if (!freshHtmlCopy.querySelector(PRODUCT_GRID_SELECTOR)) {
        console.warn('QuickAdd: Cached/fetched HTML does not contain quickview content. Aborting modal update.');
        return;
      }

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Delegated click handler for this component. Triggers quick add when
   * `.quick-add__button--choose` is activated.
   * @param {MouseEvent} event
   */
  #handleDelegatedClick = (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('.quick-add__button--choose')
      : null;

    if (!target || !this.contains(target)) return;
    this.handleClick(event);
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick add modal with the full product grid content.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector(PRODUCT_GRID_SELECTOR);
    const modalContent = document.getElementById('quick-add-modal-content');
    
    if (!productGrid || !modalContent) {
      // Log a message indicating which element is missing and why the update cannot proceed
      if (!productGrid && !modalContent) {
        console.warn('QuickAdd: updateQuickAddModal aborted - both productGrid and modalContent are missing. Cannot update modal content.');
      } else if (!productGrid) {
        console.warn('QuickAdd: updateQuickAddModal aborted - productGrid (data-product-grid-content) is missing in the new HTML. Cannot update modal content.');
      } else {
        console.warn('QuickAdd: updateQuickAddModal aborted - modalContent (#quick-add-modal-content) is missing in the DOM. Cannot update modal content.');
      }
      return;
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
